import type { SpinResponse } from "@slot/client-engine";
import { wallClockSleep } from "../boot/utils/wallClockSleep";
import type { ReplayRoundProvider } from "../replay/ReplayRoundProvider";

/** A title's offline replay rounds: the round for each known id, and the one any other id replays. */
interface FixtureReplayRounds {
  readonly roundsById: Readonly<Record<string, () => SpinResponse>>;
  readonly defaultRound: () => SpinResponse;
}

/**
 * The offline replay source a title runs on without a backend (the real path is `BackendReplayRoundProvider`): it
 * serves `?replay=<id>` and the dev `?pending=<id>` restore, both read through startGame's one provider. A known id
 * replays its round, any other id the default round, each call a fresh `SpinResponse`; `failingRoundId` (default
 * `fail`, the platform's `?replay=fail` / `?pending=fail` injection) fails the fetch. Until decision 40 each title
 * wrote its own.
 */
export class FixtureReplayRoundProvider implements ReplayRoundProvider {
  private readonly roundsById: ReadonlyMap<string, () => SpinResponse>;
  private readonly defaultRound: () => SpinResponse;
  private readonly simulatedLatencyMs: number;
  private readonly failingRoundId: string;

  public constructor(rounds: FixtureReplayRounds, options: { simulatedLatencyMs?: number; failingRoundId?: string } = {}) {
    this.roundsById = new Map(Object.entries(rounds.roundsById));
    this.defaultRound = rounds.defaultRound;
    this.simulatedLatencyMs = options.simulatedLatencyMs ?? 0;
    this.failingRoundId = options.failingRoundId ?? "fail";
  }

  public async fetchRoundForReplay(roundId: string): Promise<SpinResponse> {
    await wallClockSleep(this.simulatedLatencyMs);
    if (roundId === this.failingRoundId) throw new Error(`FixtureReplayRoundProvider: round "${roundId}" is not available`);
    return (this.roundsById.get(roundId) ?? this.defaultRound)();
  }
}
